import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, TextField, Button, Stepper, Step, StepLabel,
    Grid, Alert, Checkbox, FormControlLabel, Link, Divider, InputAdornment, IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Business, Person, Email, Phone, Home, AccountCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';

const steps = ['Informazioni Azienda', 'Dettagli Contatto', 'Account Amministratore', 'Conferma'];

const textFieldStyle = {
    '& .MuiInputLabel-root': { color: '#666', fontWeight: 600 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2', fontWeight: 700 },
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: '#ccc', borderWidth: 2 },
        '&:hover fieldset': { borderColor: '#1976d2' },
        '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: 2 },
        '& input': { color: '#333', fontWeight: 500 },
        '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: '#666' }
    },
    '& .MuiFormHelperText-root': { color: '#666', fontWeight: 500 }
};

const RegistrationPage = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    const [formData, setFormData] = useState({
        // Step 1: Informazioni Azienda
        companyName: '',
        vatNumber: '',
        
        // Step 2: Dettagli Contatto
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        
        // Step 3: Account Amministratore
        adminUsername: '',
        adminPassword: '',
        adminFullName: '',
        
        // Consensi
        acceptTerms: false,
        acceptPrivacy: false
    });

    const handleInputChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
    };

    const validateStep = (step) => {
        switch (step) {
            case 0:
                return formData.companyName.trim().length > 0;
            case 1:
                return formData.contactPerson.trim().length > 0 && 
                       formData.email.trim().length > 0 && 
                       /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
            case 2:
                return formData.adminUsername.trim().length > 0 && 
                       formData.adminPassword.length >= 6;
            case 3:
                return formData.acceptTerms && formData.acceptPrivacy;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
        } else {
            setError('Compila tutti i campi obbligatori');
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleSubmit = async () => {
        if (!validateStep(3)) {
            setError('Accetta i termini e la privacy policy per continuare');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.post('/api/register/register', formData);
            
            if (response.data.success) {
                setSuccess(true);
                setTimeout(() => {
                    navigate('/login', { 
                        state: { 
                            message: 'Registrazione completata! Puoi effettuare il login.',
                            username: formData.adminUsername 
                        }
                    });
                }, 3000);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Errore durante la registrazione');
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nome Azienda / Struttura *"
                                value={formData.companyName}
                                onChange={handleInputChange('companyName')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Business />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiInputLabel-root': { color: '#666', fontWeight: 600 },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2', fontWeight: 700 },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: '#ccc', borderWidth: 2 },
                                        '&:hover fieldset': { borderColor: '#1976d2' },
                                        '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: 2 },
                                        '& input': { color: '#333', fontWeight: 500 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Partita IVA / Codice Fiscale"
                                value={formData.vatNumber}
                                onChange={handleInputChange('vatNumber')}
                                sx={{
                                    '& .MuiInputLabel-root': { color: '#666', fontWeight: 600 },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2', fontWeight: 700 },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: '#ccc', borderWidth: 2 },
                                        '&:hover fieldset': { borderColor: '#1976d2' },
                                        '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: 2 },
                                        '& input': { color: '#333', fontWeight: 500 }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                );

            case 1:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Persona di Contatto *"
                                value={formData.contactPerson}
                                onChange={handleInputChange('contactPerson')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email *"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange('email')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Telefono"
                                value={formData.phone}
                                onChange={handleInputChange('phone')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Indirizzo"
                                value={formData.address}
                                onChange={handleInputChange('address')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Home />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                fullWidth
                                label="Città"
                                value={formData.city}
                                onChange={handleInputChange('city')}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="CAP"
                                value={formData.postalCode}
                                onChange={handleInputChange('postalCode')}
                                sx={textFieldStyle}
                            />
                        </Grid>
                    </Grid>
                );

            case 2:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="body2" gutterBottom sx={{ color: '#666' }}>
                                Crea le credenziali per l'account amministratore che gestirà la struttura
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nome Completo Amministratore"
                                value={formData.adminFullName}
                                onChange={handleInputChange('adminFullName')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Username *"
                                value={formData.adminUsername}
                                onChange={handleInputChange('adminUsername')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AccountCircle />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Password *"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.adminPassword}
                                onChange={handleInputChange('adminPassword')}
                                helperText="Minimo 6 caratteri"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>
                    </Grid>
                );

            case 3:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
                            Riepilogo Registrazione
                        </Typography>
                        
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>Azienda:</Typography>
                                <Typography variant="body1" sx={{ color: '#333' }}>{formData.companyName}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>Contatto:</Typography>
                                <Typography variant="body1" sx={{ color: '#333' }}>{formData.contactPerson}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>Email:</Typography>
                                <Typography variant="body1" sx={{ color: '#333' }}>{formData.email}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>Username Admin:</Typography>
                                <Typography variant="body1" sx={{ color: '#333' }}>{formData.adminUsername}</Typography>
                            </Grid>
                        </Grid>

                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                <strong>Periodo di Prova Gratuito:</strong><br />
                                • 30 giorni di prova gratuita<br />
                                • Massimo 3 anziani gestibili<br />
                                • Accesso a tutte le funzionalità base<br />
                            </Typography>
                        </Alert>

                        <Divider sx={{ my: 2 }} />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.acceptTerms}
                                    onChange={handleInputChange('acceptTerms')}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: '#333' }}>
                                    Accetto i <Link href="/terms" target="_blank" sx={{ fontWeight: 600 }}>Termini di Servizio</Link>
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.acceptPrivacy}
                                    onChange={handleInputChange('acceptPrivacy')}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: '#333' }}>
                                    Accetto la <Link href="/privacy" target="_blank" sx={{ fontWeight: 600 }}>Privacy Policy</Link>
                                </Typography>
                            }
                        />
                    </Box>
                );

            default:
                return null;
        }
    };

    if (success) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 2 
            }}>
                <Card sx={{ maxWidth: 500, width: '100%', bgcolor: 'rgba(255,255,255,0.98)' }}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#2e7d32' }}>
                            ✓ Registrazione Completata!
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: '#333' }}>
                            Il tuo account è stato creato con successo. Puoi ora effettuare il login e iniziare 
                            il periodo di prova gratuito di 30 giorni.
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Reindirizzamento automatico alla pagina di login...
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            py: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Box sx={{ maxWidth: 800, width: '100%', px: 2 }}>
                <Card sx={{ bgcolor: 'rgba(255,255,255,0.98)' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, color: '#1976d2' }}>
                            Registrazione SaaS Gestionale Anziani
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ mb: 4, color: '#666', fontSize: '1rem' }}>
                            Inizia il tuo periodo di prova gratuito di 30 giorni
                        </Typography>

                        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel sx={{ 
                                        '& .MuiStepLabel-label': { color: '#333', fontWeight: 600, fontSize: '0.95rem' },
                                        '& .MuiStepLabel-label.Mui-active': { color: '#1976d2', fontWeight: 700 },
                                        '& .MuiStepLabel-label.Mui-completed': { color: '#2e7d32', fontWeight: 600 }
                                    }}>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        {renderStepContent(activeStep)}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button
                                disabled={activeStep === 0}
                                onClick={handleBack}
                                variant="outlined"
                                sx={{ 
                                    color: '#333', 
                                    borderColor: '#333',
                                    fontWeight: 600,
                                    '&:hover': {
                                        borderColor: '#1976d2',
                                        color: '#1976d2'
                                    }
                                }}
                            >
                                Indietro
                            </Button>
                            
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/login')}
                                    sx={{ 
                                        color: '#333', 
                                        borderColor: '#333',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        '&:hover': {
                                            borderColor: '#1976d2',
                                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                                            color: '#1976d2'
                                        }
                                    }}
                                >
                                    Hai già un account?
                                </Button>
                                
                                {activeStep === steps.length - 1 ? (
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={loading || !validateStep(activeStep)}
                                        sx={{ fontWeight: 600, fontSize: '0.95rem' }}
                                    >
                                        {loading ? 'Registrazione...' : 'Completa Registrazione'}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!validateStep(activeStep)}
                                        sx={{ fontWeight: 600, fontSize: '0.95rem' }}
                                    >
                                        Avanti
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default RegistrationPage;